// What the Groceries screen shows (slice 6b; wireframes 6, 15, 16, 25):
// a week's lines sorted into what's left to buy by aisle, added items, and
// the folded "Already have", "In the cart" and "Not buying" groups; where
// each line is used; and the week heading. Pure functions, tested in
// tests/grocery-view.test.mjs.
import { CATEGORIES, CATEGORY_LABELS, DAYS, MEAL_SLOTS } from "./constants";
import { buildGroceryList } from "./groceryUtils";
import { groceryPlacements, type GroceryPlacement } from "./planScaling";
import { shiftWeek } from "./utils";
import { extraKey, extrasForWeek, markFor, type GroceryExtra, type GroceryLine, type Mark, type MarkRow } from "./groceryState";
import type { PlansByWeek, RecipeById } from "./types";

export interface Line extends GroceryLine {
  recipeIds: string[];
}

// A row on the screen: a recipe line or an added item, with its mark now.
export type Row = { kind: "line"; line: Line; mark: Mark | null } | { kind: "extra"; extra: GroceryExtra; mark: Mark | null };

export const rowKey = (row: Row) => (row.kind === "line" ? row.line.key : extraKey(row.extra.id));
export const rowName = (row: Row) => (row.kind === "line" ? row.line.name : row.extra.name);

// The week's recipe lines, by aisle then name, with where they came from.
export function weekLines(plansByWeek: PlansByWeek, weekStart: string, recipeById: RecipeById): { lines: Line[]; placements: GroceryPlacement[] } {
  const placements = groceryPlacements(plansByWeek, weekStart, recipeById);
  const grouped = buildGroceryList(placements, recipeById) as Record<string, Line[]>;
  const order = [...CATEGORIES, ...Object.keys(grouped).filter((c) => !CATEGORIES.includes(c))];
  return { lines: order.flatMap((c) => grouped[c] || []), placements };
}

export interface Aisle {
  category: string;
  label: string;
  rows: Row[];
}

export interface GroceryView {
  aisles: Aisle[]; // recipe lines still to buy, by aisle
  extras: Row[]; // added items still to buy
  have: Row[];
  cart: Row[];
  removed: Row[];
  recipeLineCount: number; // the week's recipe lines, whatever their mark
  extraCount: number;
}

export function groceryView(lines: Line[], allExtras: GroceryExtra[], weekStart: string, marks: Map<string, MarkRow>): GroceryView {
  const lineRows: Row[] = lines.map((line) => ({ kind: "line", line, mark: markFor(line, marks.get(line.key)) }));
  const extras = extrasForWeek(allExtras, weekStart);
  const extraRows: Row[] = extras.map((extra) => ({ kind: "extra", extra, mark: markFor({ amounts: [] }, marks.get(extraKey(extra.id))) }));
  const all = [...lineRows, ...extraRows];
  const aisles: Aisle[] = [];
  for (const row of lineRows) {
    if (row.mark || row.kind !== "line") continue;
    const category = row.line.category || "other";
    let aisle = aisles.find((a) => a.category === category);
    if (!aisle) aisles.push((aisle = { category, label: (CATEGORY_LABELS as Record<string, string>)[category] ?? category, rows: [] }));
    aisle.rows.push(row);
  }
  return {
    aisles,
    extras: extraRows.filter((r) => !r.mark),
    have: all.filter((r) => r.mark === "have"),
    cart: all.filter((r) => r.mark === "cart"),
    removed: all.filter((r) => r.mark === "removed"),
    recipeLineCount: lines.length,
    extraCount: extras.length,
  };
}

// "Lentil soup: Mon dinner, Wed lunch", "Granola bars: snacks" -- the
// recipes that need a line, and where they are planned.
export function lineSources(line: Line, placements: GroceryPlacement[], recipeById: RecipeById): { title: string; where: string }[] {
  return line.recipeIds
    .map((id) => {
      const places = placements.filter((p) => p.recipeId === id);
      const meals = places
        .filter((p) => p.day && p.slot)
        .sort((a, b) => DAYS.indexOf(a.day!) - DAYS.indexOf(b.day!) || MEAL_SLOTS.indexOf(a.slot!) - MEAL_SLOTS.indexOf(b.slot!))
        .map((p) => `${p.day!.slice(0, 3)} ${p.slot}`);
      const lists = [...new Set(places.filter((p) => p.list).map((p) => p.list!))];
      return { title: recipeById(id)?.title ?? "A recipe", where: [...meals, ...lists].join(", ") };
    })
    .sort((a, b) => a.title.localeCompare(b.title));
}

// "Groceries for this week" / "next week" / "last week" / "the week of".
export function groceryWeekLabel(weekStart: string, thisWeek: string): string {
  if (weekStart === thisWeek) return "Groceries for this week";
  if (weekStart === shiftWeek(thisWeek, 1)) return "Groceries for next week";
  if (weekStart === shiftWeek(thisWeek, -1)) return "Groceries for last week";
  return "Groceries for the week of";
}

// "3 lb", "2 cups + 100 ml"
export function amountText(amounts: { unit: string; quantity: number }[], format: (q: number) => string): string {
  return amounts
    .filter((a) => a.quantity > 0 || a.unit)
    .map((a) => `${format(a.quantity)}${a.unit ? ` ${a.unit}` : ""}`.trim())
    .join(" + ");
}
